/**
 * Scroll motion helpers shared by every chat surface.
 *
 * All programmatic smoothness is explicit JS behavior — the transcript
 * containers must NOT set CSS `scroll-behavior: smooth` (it turns every
 * corrective `scrollTop` write into an animation, which is exactly the
 * "being dragged" feel the scroll redesign removed).
 */

export function prefersReducedMotion(): boolean {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
}

/** Downgrades smooth to instant when the user prefers reduced motion. */
export function resolveScrollBehavior(
  preferred: ScrollBehavior = 'smooth',
): ScrollBehavior {
  if (preferred === 'smooth' && prefersReducedMotion()) return 'auto';
  return preferred;
}

/**
 * Behavior for a jump-to-bottom: smooth when the target is close enough to
 * read as motion (≤ 1.5 viewports away), instant beyond that — a long smooth
 * scroll through hundreds of messages is disorienting and slow.
 */
export function smartBottomBehavior(
  distancePx: number,
  clientHeight: number,
): ScrollBehavior {
  const preferred: ScrollBehavior =
    distancePx <= clientHeight * 1.5 ? 'smooth' : 'auto';
  return resolveScrollBehavior(preferred);
}
